import { ChangeEvent, FormEvent, useRef, useState } from "react";
import { CrossIcon, PlusCircleIcon } from "../Icons";
import CustomModal from "./CustomModal";
import InputWithLabel from "../InputWithLabel";
import Dropdown from "../DropDown";
import { initialRowData } from "./DealsTable";
import { toast } from "react-toastify";
import HighlightText from "../PartnerCompany/HighlightText";

type Props = {
  addColumn: (field: string, data_type?: string, options?: string[]) => void;
};

type Suggestion = { field: string; data_type: string; options?: string[] };

const EXCLUDED_FIELDS = ["payables_status", "receivables_status"];

// utils
export const formatColumnLabel = (name: string) => {
  if (!name) return "";

  return name
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export default function AddColBtn({ addColumn }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [field, setField] = useState("");
  const [dataType, setDataType] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectOptions, setSelectOptions] = useState<string[]>([]);

  const currentOptionInputRef = useRef<HTMLInputElement>(null);

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
    setSelectOptions([]);
  };

  const existingFields = Object.entries(initialRowData)
    .filter(([key]) => !EXCLUDED_FIELDS.includes(key))
    .map(([key, val]) => ({
      field: key,
      data_type: val.column_type.data_type,
      options: (val.column_type as any).options ?? [],
    }));

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setField(value);

    if (value.trim().length > 0) {
      const filtered = existingFields.filter((f) =>
        f.field.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filtered);
    } else {
      setSuggestions(existingFields);
    }
  };

  const handleSuggestionClick = (s: Suggestion) => {
    setField(s.field);
    setDataType(s.data_type);
    if (s.data_type === "select" && s.options && s.options.length > 0) {
      setSelectOptions(s.options);
    } else {
      setSelectOptions([]); // reset if not select or no options
    }

    setSuggestions([]);
  };

  const addOption = () => {
    if (!currentOptionInputRef.current) return;

    const trimedValue = currentOptionInputRef.current.value.trim();

    if (trimedValue && !selectOptions.includes(trimedValue)) {
      setSelectOptions([...selectOptions, trimedValue]);

      currentOptionInputRef.current.value = "";
    } else {
      toast.warning("Option already exists");
    }
  };

  const removeOption = (opt: string) => {
    setSelectOptions(selectOptions.filter((o) => o !== opt));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    addColumn(field, dataType, selectOptions);

    // Auto-add status column for payables/receivables
    if (field === "payables" || field === "receivables") {
      addColumn(`${field}_status`, "select", ["Pending", "Paid"]);
    }

    setField("");
    setDataType("");
    toggleModal();
  };

  return (
    <div>
      <button
        className="text-gray-500 hover:scale-90 hover:text-gray-700"
        onClick={toggleModal}
        title="Add a new column"
      >
        <PlusCircleIcon className="size-6" />
      </button>

      <CustomModal open={isModalOpen} onClose={toggleModal}>
        <div className="mb-4">
          <h2 className="font-medium text-xl text-gray-950">Add Column</h2>
          <p className="text-xs text-gray-600">
            Focus the input to see all fields or start typing to filter them.
            Select one to auto-fill details or create a new column.
          </p>
        </div>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="relative">
            <InputWithLabel
              label="Column Name"
              name="field"
              placeholder="Enter here"
              required
              minLength={2}
              value={field}
              onChange={handleInputChange}
              onFocus={() => {
                if (suggestions.length === 0) {
                  setSuggestions(existingFields);
                }
              }}
              onBlur={() => {
                setTimeout(() => setSuggestions([]), 150);
              }}
              autoComplete="off"
            />
            {suggestions.length > 0 && (
              <ul className="absolute z-10 bg-white border rounded-md w-full max-h-40 overflow-y-auto shadow-md">
                {suggestions.map((s) => (
                  <li
                    key={s.field}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                    // onClick={() => handleSuggestionClick(s)}
                    onMouseDown={() => handleSuggestionClick(s)}
                  >
                    <HighlightText
                      text={formatColumnLabel(s.field)}
                      query={field}
                    />{" "}
                    <span className="text-xs text-gray-500">
                      ({s.data_type})
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Dropdown
            label="Column Type"
            name="data_type"
            value={dataType}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setDataType(e.target.value)
            }
            options={[
              { label: "Text", value: "string" },
              { label: "Date", value: "date" },
              { label: "Dropdown", value: "select" },
              { label: "File", value: "file" },
            ]}
          />

          {dataType === "select" && field !== "flow" && (
            <div>
              <label className="text-[#344054] text-sm font-medium mb-1">
                Dropdown Options
              </label>
              <div className="flex gap-2">
                <input
                  ref={currentOptionInputRef}
                  type="text"
                  // value={currentOption}
                  // onChange={(e) => setCurrentOption(e.target.value)}
                  placeholder="Type option & press Add"
                  className="input-new"
                  required={selectOptions.length === 0}
                />
                <button
                  type="button"
                  onClick={addOption}
                  className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm"
                >
                  Add
                </button>
              </div>

              {/* Show added options */}
              <div className="flex flex-wrap gap-2 mt-2">
                {selectOptions.map((opt) => (
                  <span
                    key={opt}
                    className="flex items-center gap-1 bg-gray-200 text-gray-800 px-2 py-1 rounded-lg text-sm"
                  >
                    {opt}
                    <button
                      type="button"
                      onClick={() => removeOption(opt)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <CrossIcon className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="button"
              className="w-full text-gray-900 p-2 cursor-pointer border border-gray-900 rounded-xl"
              onClick={toggleModal}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full bg-custom-blue-medium text-white p-2 cursor-pointer rounded-xl"
            >
              Save
            </button>
          </div>
        </form>
      </CustomModal>
    </div>
  );
}
